<template>
  <v-card elevation="2">
    <v-card-title class="d-flex align-center">
      <v-icon class="mr-2">mdi-comment-question</v-icon>
      Pregunta Actual que se muestra a los estudiantes
    </v-card-title>

    <v-card-subtitle class="d-flex justify-end">
      <v-btn color="primary" variant="outlined" size="small" @click="printCurrentQuestion">
        <v-icon class="mr-2">mdi-file-pdf-box</v-icon>
        Imprimir Pregunta
      </v-btn>
    </v-card-subtitle>

    <!-- Loading -->
    <v-card-text v-if="loading">
      <div class="text-center pa-8">
        <v-progress-circular indeterminate color="primary" size="64" />
        <div class="mt-4">Cargando pregunta...</div>
      </div>
    </v-card-text>

    <!-- No question selected -->
    <v-card-text v-else-if="!question">
      <v-alert type="info" variant="tonal">
        <v-icon>mdi-information</v-icon>
        No hay pregunta seleccionada para esta sesión.
      </v-alert>
    </v-card-text>

    <!-- Question content -->
    <v-card-text v-else ref="printArea">
      <!-- Basic information chips -->
      <div class="d-flex flex-wrap gap-2 mb-4">
        <v-chip
          v-if="question.school_subject?.name"
          color="primary"
          variant="outlined"
          prepend-icon="mdi-book-open-variant"
        >
          {{ question.school_subject.name }}
        </v-chip>
        <v-chip v-else color="grey" variant="outlined" prepend-icon="mdi-book-off">
          Sin asignatura
        </v-chip>

        <v-chip color="info" variant="outlined" prepend-icon="mdi-clock">
          {{ formatTime(question.time_limit) }}
        </v-chip>

        <v-chip
          v-if="question.school_grade?.grade"
          color="success"
          variant="outlined"
          prepend-icon="mdi-school"
        >
          Grado {{ question.school_grade?.grade }}
        </v-chip>
      </div>

      <!-- Question statement -->
      <div class="question-statement mb-4">
        <h3 class="mb-3">Pregunta:</h3>
        <div class="text-body-1 text-justify">
          <MathRenderer :content="question.statement" />
        </div>
      </div>

      <!-- Statement image -->
      <div v-if="question.statement_image_url" class="mb-4">
        <AuthenticatedImage
          :src="question.statement_image_url"
          max-height="400"
          image-class="border rounded"
        />
      </div>

      <!-- Answer options -->
      <div class="mt-4">
        <h4 class="mb-3">
          <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
          Opciones de Respuesta
        </h4>
        <v-row>
          <v-col
            v-for="(option, index) in question.options"
            :key="option.id || index"
            cols="12"
            md="6"
          >
            <v-card class="option-card pa-3" variant="outlined">
              <div class="d-flex align-center">
                <v-avatar size="32" color="grey-lighten-2" class="mr-3">
                  <span class="text-h6 font-weight-bold">
                    {{ option.label }}
                  </span>
                </v-avatar>
                <div class="flex-grow-1">
                  <!-- Option text -->
                  <div v-if="option.text" class="option-text">
                    <MathRenderer :content="option.text" />
                  </div>
                  <!-- Option image -->
                  <div v-if="option.image_url" class="option-image">
                    <AuthenticatedImage
                      :src="option.image_url"
                      max-height="120"
                      image-class="border rounded"
                    />
                  </div>
                  <div v-if="!option.text && !option.image_url" class="text-disabled">
                    Opción sin contenido
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </v-card-text>

    <!-- Error -->
    <v-card-text v-if="error">
      <v-alert type="error" variant="tonal">
        <v-icon>mdi-alert-circle</v-icon>
        {{ error }}
      </v-alert>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { computed, onMounted, watch, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import AuthenticatedImage from '@/components/common/AuthenticatedImage.vue'
import MathRenderer from '@/components/common/MathRenderer.vue'
import { ref } from 'vue'

const printArea = ref(null)

const props = defineProps({
  schoolGradeId: {
    type: [Number, String],
    required: true,
  },
})

const store = useStore()

// Computed properties
const loading = computed(() => store.getters['questions/loading'])
const error = computed(() => store.getters['questions/error'])
const questions = computed(() => store.getters['questions/questions'])
const groupId = computed(() => store.getters['session/getGroupId'])
const pinCode = computed(() => store.getters['session/getPinCode'])
const userRole = computed(() => store.state.auth.user?.role)

// Get the first question from the list (current question for the session)
const question = computed(() => {
  if (questions.value && questions.value.length > 0) {
    // Return first question without showing correct answer
    const q = { ...questions.value[0] }
    // Filter out is_correct from options to hide correct answer
    if (q.options && Array.isArray(q.options)) {
      q.options = q.options.map((option) => {
        const { is_correct, ...publicOption } = option
        return publicOption
      })
    }
    return q
  }
  return null
})

// Methods
const formatTime = (seconds) => {
  if (!seconds) return 'N/D'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Function to load questions
const loadQuestions = async () => {
  if (props.schoolGradeId) {
    const params = {
      schoolGradeId: props.schoolGradeId,
    }

    // Teachers/admins must pass pinCode to see the same question as students
    if (userRole.value === 'teacher' || userRole.value === 'admin') {
      params.pinCode = pinCode.value
    }

    await store.dispatch('questions/fetchQuestionsBySchoolGrade', params)
  }
}

// Clear questions from store
const clearQuestions = () => {
  store.commit('questions/SET_QUESTIONS', [])
  store.commit('questions/CLEAR_ERROR')
}

// Watch for changes in schoolGradeId or pinCode
watch(
  [() => props.schoolGradeId, pinCode],
  async ([newSchoolGradeId, newPinCode], [oldSchoolGradeId, oldPinCode]) => {
    // Only reload if values actually changed
    if (newSchoolGradeId !== oldSchoolGradeId || newPinCode !== oldPinCode) {
      clearQuestions()
      await loadQuestions()
    }
  },
)

// Load questions on mount
onMounted(async () => {
  await loadQuestions()
})

// Clean up on unmount
onBeforeUnmount(() => {
  clearQuestions()
})

const printCurrentQuestion = () => {
  const element = printArea.value?.$el || printArea.value
  const printContent = element?.innerHTML

  if (!printContent) {
    alert('No hay contenido para imprimir. Por favor, asegúrese de que la pregunta esté cargada.')
    return
  }

  const printWindow = window.open('', '', 'width=900,height=700')

  let styles = ''
  document.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
    styles += node.outerHTML
  })

  printWindow.document.write(`
    <html>
      <head>
        <title>Impresión de Pregunta</title>
        ${styles}
        <style>
          /* Ajustes específicos para impresión */
          body {
            margin: 20px;
            background: white;
            color: #000;
          }

          .v-container,
          .v-card {
            box-shadow: none !important;
          }

          .v-card {
            border: 1px solid #ddd !important;
            margin-bottom: 16px;
            page-break-inside: avoid;
          }

          .correct-option {
            border: 2px solid #4caf50 !important;
            background-color: #f1f8f4 !important;
          }

          img, .v-img {
            max-width: 100%;
            height: auto;
          }

          /* Oculta elementos que no son relevantes en impresión */
          .v-btn,
          .v-icon.mdi-arrow-left,
          nav,
          aside {
            display: none !important;
          }

          /* Evita que los textos se corten entre páginas */
          .v-card, .option-card {
            page-break-inside: avoid;
          }

          @page {
            margin: 2cm;
          }
        </style>
      </head>
      <body>
        ${printContent}
      </body>
    </html>
  `)

  printWindow.document.close()
  printWindow.focus()

  setTimeout(() => {
    printWindow.print()
    printWindow.close()
  }, 600)
}
</script>

<style scoped>
.question-statement {
  background: rgba(var(--v-theme-primary), 0.05);
  border-radius: 8px;
  padding: 16px;
}

.option-card {
  transition: all 0.2s ease;
  background-color: white;
}

.option-card:hover {
  elevation: 2;
  transform: translateY(-1px);
}

.border {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.rounded {
  border-radius: 4px;
}

.d-flex.gap-2 > * {
  margin-right: 8px;
}

.option-text {
  font-size: 1rem;
  line-height: 1.5;
}

.option-image {
  max-width: 100%;
}
</style>
